import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { impersonate } from 'api/Admin/Practices/Members';
import { deleteMember, updateStatusMember } from 'api/Member';

import { useTableContext } from 'lib/context/TableContext/TableContext';

import adminState from 'components/admin/lib/adminState';
import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import SupervisorSettings from 'components/Staff/components/SupervisorSettings';

const Actions = ({ data, principalUserId }) => {
  const { user_id } = data;
  const isPrincipal = principalUserId && user_id === principalUserId;
  const { setSelectedItem, setShowModal } = useTableContext();
  const { practice } = useOutletContext();
  const navigate = useNavigate(null);
  const queryClient = useQueryClient();
  const admin_state = useRecoilValue(adminState.current_admin);
  const isAdmin = admin_state?.logged_in;
  const [showSupervisorSettings, setShowSupervisorSettings] = useState(false);
  const [showConfirmImpersonate, setShowConfirmImpersonate] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmStatusUpdate, setShowConfirmStatusUpdate] = useState(false);
  const handleImpersonate = async () => {
    sessionStorage.removeItem('myriad-session-reference-id');
    await impersonate(navigate, { targetId: data?.user_id || data?.id });
    setShowConfirmImpersonate(false);
  };

  const mutateDelete = useMutation({
    mutationFn: () =>
      deleteMember(navigate, { memberId: data?.user_id || data?.id, practiceId: practice?.id }),
    onSuccess: (res) => {
      if (res?.code === 0) {
        queryClient.invalidateQueries(['member']);
        showAlert({ title: 'Member deleted', color: 'success' });
      } else {
        showAlert({ title: "Couldn't delete member", color: 'danger' });
      }
      setShowConfirmDelete(false);
    }
  });
  const mutateMemberStatus = useMutation({
    mutationFn: () =>
      updateStatusMember(navigate, {
        userIds: [data?.user_id || data?.id],
        practiceId: practice?.id,
        status: data.status === 'active' ? 'inactive' : 'active'
      }),
    onSuccess: (res) => {
      if (res?.code === 0) {
        queryClient.invalidateQueries(['member']);
        showAlert({ title: 'Member status updated', color: 'success' });
      } else {
        showAlert({ title: "Couldn't update member status", color: 'danger' });
      }
      setShowConfirmStatusUpdate(false);
    },
    onError: () => {
      showAlert({ title: "Couldn't update member status", color: 'danger' });
      setShowConfirmStatusUpdate(false);
    }
  });

  const actionOptions = [
    ...(isAdmin
      ? [
          {
            label: 'Impersonate',
            icon: 'mask-mono',
            onClick: () => setShowConfirmImpersonate(true)
          },
          {
            label: 'View profile',
            icon: 'eye-mono',
            onClick: () => {
              navigate(`/admin/practitioners/${data.user_id}`);
            }
          }
        ]
      : []),
    {
      label: 'Edit',
      icon: 'edit-2-mono',
      onClick: () => {
        setShowModal(true);
        setSelectedItem(data);
      },
      permissions: {
        requiredPermissions: 'member.update'
      }
    },
    {
      label: 'Make supervisor',
      icon: 'user-octagon-mono',
      onClick: () => setShowSupervisorSettings(true),
      permissions: {
        requiredPermissions: 'member.update'
      }
    },
    ...((isPrincipal && isAdmin) || !isPrincipal
      ? [
          {
            label: data.status === 'active' ? 'Deactivate' : 'Activate',
            icon: data.status === 'active' ? 'minus-circle-mono' : 'tick-circle-mono',
            onClick: () => setShowConfirmStatusUpdate(true),
            permissions: {
              requiredPermissions: 'member.update'
            }
          }
        ]
      : []),
    {
      label: 'Delete',
      icon: 'trash-mono',
      onClick: () => {
        setShowConfirmDelete(true);
      },
      permissions: {
        requiredPermissions: 'member.delete'
      }
    }
  ];

  return (
    <>
      <Popover
        isFixed
        position="left"
        isBorder={false}
        options={actionOptions}
        className="flex h-full flex-col justify-center"
      />
      {showSupervisorSettings && (
        <SupervisorSettings
          member={data}
          showModal={showSupervisorSettings}
          hideModal={() => setShowSupervisorSettings(false)}
        />
      )}
      {showConfirmImpersonate && (
        <Confirm
          variant="primary"
          title="Impersonate provider"
          icon="eye-mono"
          message="You're about to impersonate a provider. Your permissions are limited per HIPAA compliance and activity is logged."
          handleOpen={showConfirmImpersonate}
          handleClose={() => setShowConfirmImpersonate(false)}
          footerActions={{
            primary: {
              text: 'Confirm',
              onClick: handleImpersonate
            }
          }}
        />
      )}
      {showConfirmDelete && (
        <Confirm
          variant="danger"
          title="Delete member"
          icon="trash-mono"
          message="Are you sure you want to delete this member?"
          handleOpen={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          footerActions={{
            primary: {
              text: 'Delete',
              onClick: () => mutateDelete.mutate(),
              color: 'danger'
            }
          }}
        />
      )}
      {showConfirmStatusUpdate && (
        <Confirm
          variant={data.status === 'active' ? 'danger' : 'success'}
          title={data?.status === 'active' ? 'Deactivate member' : 'Activate member'}
          icon={data.status === 'active' ? 'minus-circle-mono' : 'tick-circle-mono'}
          message="Are you sure you want to update this member's status?"
          handleOpen={showConfirmStatusUpdate}
          handleClose={() => setShowConfirmStatusUpdate(false)}
          footerActions={{
            primary: {
              text: data?.status === 'active' ? 'Deactivate' : 'Activate',
              onClick: () => mutateMemberStatus.mutate(),
              color: data.status === 'active' ? 'danger' : 'success'
            }
          }}
        />
      )}
    </>
  );
};

export default Actions;
